import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  MapPin, Phone, Mail, Globe, Clock, ArrowLeft, 
  Share2, ExternalLink, Navigation, Building,
  X, ChevronLeft, ChevronRight, ZoomIn
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { businessesApi, categoriesApi, departmentsApi } from "@/lib/api";

// Lightbox Component for photo gallery
const PhotoLightbox = ({ photos, currentIndex, onClose, onPrev, onNext }) => {
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onPrev, onNext]);

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
        data-testid="lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 text-white/80 text-sm font-medium bg-black/50 px-3 py-1 rounded-full">
        {currentIndex + 1} / {photos.length}
      </div>

      {/* Previous button */}
      {photos.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}

      {/* Image */}
      <div 
        className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={photos[currentIndex]}
          alt={`Photo ${currentIndex + 1}`}
          className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
        />
      </div>

      {/* Next button */}
      {photos.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {/* Thumbnails */}
      {photos.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/50 p-2 rounded-lg max-w-[90vw] overflow-x-auto">
          {photos.map((photo, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); onNext(idx - currentIndex); }}
              className={`w-16 h-12 rounded overflow-hidden flex-shrink-0 border-2 ${
                idx === currentIndex ? "border-[#FFD700]" : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              <img src={photo} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function BusinessPage() {
  const { id } = useParams();
  const [business, setBusiness] = useState(null);
  const [category, setCategory] = useState(null);
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "";
  };

  const goToPrev = useCallback(() => {
    if (!business?.photos?.length) return;
    setLightboxIndex((prev) => (prev === 0 ? business.photos.length - 1 : prev - 1));
  }, [business?.photos?.length]);

  const goToNext = useCallback((jump = 1) => {
    if (!business?.photos?.length) return;
    if (typeof jump === "number" && jump !== 1) {
      setLightboxIndex((prev) => {
        const newIndex = prev + jump;
        if (newIndex < 0) return business.photos.length + newIndex;
        if (newIndex >= business.photos.length) return newIndex - business.photos.length;
        return newIndex;
      });
    } else {
      setLightboxIndex((prev) => (prev === business.photos.length - 1 ? 0 : prev + 1));
    }
  }, [business?.photos?.length]);

  useEffect(() => {
    const loadBusiness = async () => {
      try {
        const res = await businessesApi.getOne(id);
        setBusiness(res.data);

        // Load category and department
        const [catRes, deptRes] = await Promise.all([
          categoriesApi.getOne(res.data.category_id).catch(() => null),
          departmentsApi.getOne(res.data.department_code).catch(() => null),
        ]);

        if (catRes) setCategory(catRes.data);
        if (deptRes) setDepartment(deptRes.data);
      } catch (error) {
        console.error("Error loading business:", error);
      } finally {
        setLoading(false);
      }
    };

    loadBusiness();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="skeleton h-64 rounded-xl mb-8"></div>
            <div className="skeleton h-8 w-1/2 mb-4"></div>
            <div className="skeleton h-4 w-full mb-2"></div>
            <div className="skeleton h-4 w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Entreprise non trouvée</h1>
          <p className="text-slate-600 mb-6">Cette entreprise n'existe pas ou a été supprimée.</p>
          <Link to="/recherche">
            <Button>Retour à la recherche</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const defaultBanner = "https://images.unsplash.com/photo-1657352945635-37458cac41a3?w=1200";
  const defaultLogo = "https://images.unsplash.com/photo-1729830375022-b0248100c501?w=200";
  
  // Use category image as fallback
  const bannerUrl = business.banner_url || category?.image_url || defaultBanner;
  const logoUrl = business.logo_url || category?.image_url || defaultLogo;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="business-page">
      <Header />

      {/* Banner */}
      <div className="relative h-64 md:h-80 bg-slate-900">
        <img
          src={bannerUrl}
          alt={business.name}
          className="w-full h-full object-cover opacity-60"
          onError={(e) => {
            e.target.src = category?.image_url || defaultBanner;
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
        
        {/* Back Button */}
        <Link 
          to="/recherche" 
          className="absolute top-4 left-4 flex items-center gap-2 text-white/80 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
          Retour
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-lg -mt-24 relative z-10 p-6 md:p-8">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Logo */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden bg-white border-2 border-slate-200 shadow-md">
                <img
                  src={logoUrl}
                  alt={business.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = category?.image_url || defaultLogo;
                  }}
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-start gap-2 mb-2">
                {category && (
                  <Badge variant="secondary" className="text-xs">
                    {category.name}
                  </Badge>
                )}
                {business.has_website_creation && (
                  <span className="badge-yellow text-xs">Site Web créé par nos soins</span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-testid="business-name">
                {business.name}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-slate-600">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-[#FFD700]" />
                  {business.city}
                  {department && ` (${department.name})`}
                </span>
              </div>

              {/* Additional cities */}
              {business.additional_cities?.length > 0 && (
                <div className="mt-2 text-sm text-slate-500">
                  Également présent à : {business.additional_cities.join(", ")}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              {business.phone && (
                <a href={`tel:${business.phone}`}>
                  <Button className="w-full bg-[#FFD700] text-slate-900 hover:bg-[#E6C200] font-bold" data-testid="call-btn">
                    <Phone className="w-4 h-4 mr-2" />
                    Appeler
                  </Button>
                </a>
              )}
              {business.website && (
                <a href={business.website} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full" data-testid="website-btn">
                    <Globe className="w-4 h-4 mr-2" />
                    Site web
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </a>
              )}
              <Button variant="ghost" className="w-full">
                <Share2 className="w-4 h-4 mr-2" />
                Partager
              </Button>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 py-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Description</h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line" data-testid="business-description">
                {business.description}
              </p>
            </div>

            {/* Additional Activities */}
            {business.additional_activities?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Activités complémentaires</h2>
                <div className="flex flex-wrap gap-2">
                  {business.additional_activities.map((activity, idx) => (
                    <Badge key={idx} variant="secondary" className="text-sm">
                      {activity}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Photos */}
            {business.photos?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  Photos
                  <span className="text-sm font-normal text-slate-500">
                    ({business.photos.length} photo{business.photos.length > 1 ? "s" : ""})
                  </span>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {business.photos.map((photo, idx) => (
                    <button
                      key={idx}
                      onClick={() => openLightbox(idx)}
                      className="aspect-square rounded-lg overflow-hidden bg-slate-100 relative group cursor-pointer"
                      data-testid={`photo-${idx}`}
                    >
                      <img
                        src={photo}
                        alt={`${business.name} - Photo ${idx + 1}`}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
                </div>
                <p className="text-sm text-slate-500 mt-3">Cliquez sur une photo pour l'agrandir</p>
              </div>
            )}

            {/* Lightbox */}
            {lightboxOpen && business.photos?.length > 0 && (
              <PhotoLightbox
                photos={business.photos}
                currentIndex={lightboxIndex}
                onClose={closeLightbox}
                onPrev={goToPrev}
                onNext={goToNext}
              />
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-4">Coordonnées</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#FFD700] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-slate-900">{business.address}</p>
                    <p className="text-slate-600">{business.postal_code} {business.city}</p>
                  </div>
                </li>
                {business.phone && (
                  <li className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-[#2563EB] flex-shrink-0" />
                    <a href={`tel:${business.phone}`} className="text-[#2563EB] font-mono hover:underline">
                      {business.phone}
                    </a>
                  </li>
                )}
                {business.email && (
                  <li className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <a href={`mailto:${business.email}`} className="text-slate-600 hover:text-[#2563EB]">
                      {business.email}
                    </a>
                  </li>
                )}
                {business.website && (
                  <li className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <a 
                      href={business.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#2563EB] hover:underline truncate"
                    >
                      {business.website.replace(/^https?:\/\//, "")}
                    </a>
                  </li>
                )}
              </ul>
            </div>

            {/* Map */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {business.latitude && business.longitude ? (
                <div className="relative">
                  <iframe
                    title={`Carte - ${business.name}`}
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${business.longitude - 0.01}%2C${business.latitude - 0.01}%2C${business.longitude + 0.01}%2C${business.latitude + 0.01}&layer=mapnik&marker=${business.latitude}%2C${business.longitude}`}
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full"
                  />
                  <div className="absolute bottom-2 right-2 flex gap-2">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${business.latitude},${business.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-[#FFD700] hover:bg-[#E6C200] text-slate-900 text-xs font-bold rounded-md shadow-md flex items-center gap-1"
                    >
                      <Navigation className="w-3 h-3" />
                      Itinéraire
                    </a>
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center bg-slate-100">
                  <div className="text-center">
                    <MapPin className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Coordonnées non disponibles</p>
                    <p className="text-xs text-slate-400 mt-1">{business.address}, {business.city}</p>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
